import type {
  InteractiveQuestionAnswerPayload as InteractiveQuestionAnswerPayloadContract,
  PostScoreableInteractiveQuestionAnswerRequestBody,
} from '@genially/contracts';

export enum PlayUserType {
  Host = 'host',
  Player = 'player',
}

export type HostPeerMetadata = {
  type: PlayUserType.Host;
  gameVersion: number;
};

export type PlayerPeerMetadata = {
  type: PlayUserType.Player;
  username: string;
  gameVersion: number;
};

export type PlayerData = {
  id: string;
  username: string;
};

export type InteractiveQuestionAnswerPayload = InteractiveQuestionAnswerPayloadContract;

export type PlayerUserAnswer<
  InteractiveQuestionAnswerPayloadType extends
    InteractiveQuestionAnswerPayload = InteractiveQuestionAnswerPayload,
> =
  InteractiveQuestionAnswerPayloadType extends PostScoreableInteractiveQuestionAnswerRequestBody
    ? {
        player: PlayerData;
        answer: InteractiveQuestionAnswerPayloadType;
        isCorrect: boolean;
      }
    : {
        player: PlayerData;
        answer: InteractiveQuestionAnswerPayloadType;
      };

export const isPlayerUserAnswerScoreable = (
  answer: PlayerUserAnswer,
): answer is PlayerUserAnswer<PostScoreableInteractiveQuestionAnswerRequestBody> =>
  'isCorrect' in answer;

export type InteractiveQuestionState<
  InteractiveQuestionAnswerPayloadType extends
    InteractiveQuestionAnswerPayload = InteractiveQuestionAnswerPayload,
> = {
  playerUserAnswers: PlayerUserAnswer<InteractiveQuestionAnswerPayloadType>[];
  isResolved: boolean;
};

export type InteractiveQuestionsState<
  InteractiveQuestionAnswerPayloadType extends
    InteractiveQuestionAnswerPayload = InteractiveQuestionAnswerPayload,
> = Record<string, InteractiveQuestionState<InteractiveQuestionAnswerPayloadType>>;

export type GameState = {
  isTrackedInLogRocket: boolean;
  isStarted: boolean;
  isFinished: boolean;
  startedAt: number | undefined;
  finishedAt: number | undefined;
  isSessionBlocked: boolean;

  currentSlide: string;
  version: number;

  interactiveQuestionAnswers: InteractiveQuestionsState;
};

export type GameStateCompressed = string;

export const isGameState = (game: unknown): game is GameState =>
  typeof game === 'object' &&
  game !== null &&
  'isStarted' in game &&
  'isFinished' in game &&
  'isSessionBlocked' in game &&
  'currentSlide' in game &&
  'version' in game &&
  'interactiveQuestionAnswers' in game;

// Host messages

export enum PlayP2pHostMessageType {
  KickPlayer = 'kick-player',
  SendGameState = 'send-game-state',
  SendGameStatePatches = 'send-game-state-patches',
  RequestGameState = 'request-game-state',
  AllowPlayerToJoin = 'allow-player-to-join',
  SessionBlocked = 'session-blocked',
  PlayersLimitReached = 'players-limit-reached',
  InteractiveQuestionResolved = 'interactive-question-resolved',
  ToggleOpenAnswerVisibility = 'toggle-open-answer-visibility',

  ChangeSlideCommand = 'change-slide',

  Benchmark = 'benchmark',
}

export type GameVersionMetadataPayload<T> = T & {
  gameVersion: number;
};

export type SessionBlockPlayerMessage = {
  type: PlayP2pHostMessageType.SessionBlocked;
  payload: undefined;
};

export type PlayersLimitReachedMessage = {
  type: PlayP2pHostMessageType.PlayersLimitReached;
  payload: undefined;
};

export type KickPlayerMessage = {
  type: PlayP2pHostMessageType.KickPlayer;
  payload: undefined;
};

export type ChangeSlideCommandMessage = {
  type: PlayP2pHostMessageType.ChangeSlideCommand;
  payload: {
    slide: string;
  };
};

export type HostSendGameStateMessage = {
  type: PlayP2pHostMessageType.SendGameState;
  payload: {
    game: GameState | GameStateCompressed;
  };
};

export type GameStatePatch =
  | {
      path: readonly (string | number)[];
      op: 'add';
      value: any;
    }
  | {
      path: readonly (string | number)[];
      op: 'replace';
      value: any;
    }
  | {
      path: readonly (string | number)[];
      op: 'remove';
    };

export type GameStatePatchWithVersion = {
  patches: readonly GameStatePatch[];
  version: number;
};

export type HostSendGameStatePatchesMessage = {
  type: PlayP2pHostMessageType.SendGameStatePatches;
  payload: GameStatePatchWithVersion[];
};

export type RequestGameStateMessage = {
  type: PlayP2pHostMessageType.RequestGameState;
  payload: undefined;
};

export type HostAllowPlayerToJoinMessage = {
  type: PlayP2pHostMessageType.AllowPlayerToJoin;
  payload: {
    featureFlags: string[];
  };
};

export type HostBenchmarkMessage = {
  type: PlayP2pHostMessageType.Benchmark;
  payload: {
    id: string;
    numMessages: number;
    message: string;
    sleepBetweenMessages: boolean;
  };
};

export type InteractiveQuestionResolvedMessage = {
  type: PlayP2pHostMessageType.InteractiveQuestionResolved;
  payload: {
    interactiveQuestionId: string;
    isLastScored?: boolean;
  };
};

export type HostToggleOpenAnswerVisibilityMessage = {
  type: PlayP2pHostMessageType.ToggleOpenAnswerVisibility;
  payload: {
    interactiveQuestionId: string;
    userAlias: string;
    isHidden: boolean;
  };
};

export type PlayP2pHostMessage = GameVersionMetadataPayload<
  | ChangeSlideCommandMessage
  | HostSendGameStateMessage
  | KickPlayerMessage
  | SessionBlockPlayerMessage
  | PlayersLimitReachedMessage
  | InteractiveQuestionResolvedMessage
  | HostBenchmarkMessage
  | HostAllowPlayerToJoinMessage
  | HostToggleOpenAnswerVisibilityMessage
  | HostSendGameStatePatchesMessage
  | RequestGameStateMessage
>;

// Player messages

export enum PlayP2pPlayerMessageType {
  SendGameState = 'send-game-state',
  RequestGameState = 'request-game-state',
  SendInteractiveQuestionAnswer = 'send-interactive-question-answer',
  SendScoreableInteractiveQuestionAnswer = 'send-scoreable-interactive-question-answer',
  Benchmark = 'benchmark',
}

export type PlayerSendGameStateMessage = {
  type: PlayP2pPlayerMessageType.SendGameState;
  payload: {
    game: GameState | GameStateCompressed;
  };
};

export type PlayerRequestGameStateMessage = {
  type: PlayP2pPlayerMessageType.RequestGameState;
  payload: undefined;
};

export type PlayerBenchmarkMessage = {
  type: PlayP2pPlayerMessageType.Benchmark;
  payload: {
    id: string;
    slept: number;
    message: string;
  };
};

export type PlayerSendInteractiveQuestionAnswerMessage = {
  type: PlayP2pPlayerMessageType.SendInteractiveQuestionAnswer;
  payload: InteractiveQuestionAnswerPayload;
};

export type PlayerSendScoreableInteractiveQuestionAnswerMessage = {
  type: PlayP2pPlayerMessageType.SendScoreableInteractiveQuestionAnswer;
  payload: { answer: InteractiveQuestionAnswerPayload; isCorrect: boolean };
};

export type PlayP2pPlayerMessage = GameVersionMetadataPayload<
  | PlayerSendGameStateMessage
  | PlayerSendInteractiveQuestionAnswerMessage
  | PlayerSendScoreableInteractiveQuestionAnswerMessage
  | PlayerBenchmarkMessage
  | PlayerRequestGameStateMessage
>;

export const isPlayP2pHostMessage = (message: unknown): message is PlayP2pHostMessage =>
  typeof message === 'object' &&
  message !== null &&
  'type' in message &&
  typeof message.type === 'string' &&
  Object.values(PlayP2pHostMessageType).includes(message.type as any);

export const isPlayP2pPlayerMessage = (
  message: unknown,
): message is PlayP2pPlayerMessage =>
  typeof message === 'object' &&
  message !== null &&
  'type' in message &&
  typeof message.type === 'string' &&
  Object.values(PlayP2pPlayerMessageType).includes(message.type as any);

export type PlayP2pMessage = PlayP2pHostMessage | PlayP2pPlayerMessage;

export const isPlayP2pMessage = (message: unknown): message is PlayP2pMessage =>
  isPlayP2pHostMessage(message) || isPlayP2pPlayerMessage(message);
